/**
 * IPC handler — platform-agnostic bridge.
 *
 * Uses the platform abstraction layer to work on Desktop (Tauri), Web, and
 * Mobile (Capacitor). On Desktop the Tauri APIs are called; on Web and
 * Mobile the calls become safe no-ops or browser equivalents.
 *
 * ### Provided functions (unchanged public API)
 * - `getMachineId()`                — fetches device ID via platform adapter
 * - `registerIpcEvents()`           — registers app-close listener
 * - `unregisterIpcEvents()`         — cleans up listeners
 * - `sendAppExitConfirmation(bool)` — closes the window (desktop) or no-op
 */
import { platform, isDesktop } from "@platform";
import { machineIdReducer } from "@store/machine-id-slice";
import { store } from "@store/index";
import {
  exitAppReducer,
  confirmDownloadReducer,
  downloadProgressReducer,
} from "@store/ipc-events-modal-splice";
import { setAmibrokerInitialized } from "@store/amibroker-slice";

import { invoke } from "@tauri-apps/api/core";

let cleanupFns: Array<() => void> = [];

/**
 * Retrieve a stable device identifier via the platform adapter and store
 * it in the `machineId` Redux slice.
 *
 * - Desktop: Tauri `get_machine_id` command (hardware UUID).
 * - Web:     Persistent random UUID in localStorage.
 * - Mobile:  Capacitor Device plugin or persistent UUID.
 */
export const getMachineId = async () => {
  try {
    const machineId = await platform.getDeviceId();
    store.dispatch(machineIdReducer({ machineId }));
  } catch (error) {
    console.error("Failed to get device ID:", error);
  }
};

/**
 * Register platform lifecycle events and map them to Redux actions.
 *
 * On Desktop this also registers Tauri-specific events (auto-update, progress).
 * On Web/Mobile only the app-close handler is relevant.
 */
export const registerIpcEvents = async () => {
  // App close / beforeunload → show exit confirmation on desktop
  const unlistenClose = platform.onAppClose(() => {
    if (isDesktop) {
      store.dispatch(exitAppReducer({ exitApp: true }));
    }
  });
  cleanupFns.push(unlistenClose);

  // Desktop-only: auto-update events via Tauri listen()
  if (isDesktop) {
    try {
      const { listen } = await import("@tauri-apps/api/event");
      const unlistenDownload = await listen("confirm-download-update", () => {
        store.dispatch(confirmDownloadReducer({ confirmDownload: true }));
      });
      const unlistenProgress = await listen("open-progress-bar", () => {
        store.dispatch(downloadProgressReducer({ downloadProgress: true }));
      });
      const unlistenAmiDied = await listen("amibroker-died", () => {
        store.dispatch(setAmibrokerInitialized(false));
      });
      cleanupFns.push(unlistenDownload, unlistenProgress, unlistenAmiDied);
    } catch {
      // Tauri not available — ignore
    }
  }
};

/** Remove all registered lifecycle listeners. Called on logout. */
export const unregisterIpcEvents = () => {
  cleanupFns.forEach((fn) => fn());
  cleanupFns = [];
};

/**
 * Respond to the app-exit confirmation dialog.
 *
 * @param exitConfirmation - `true` to exit the app, `false` to cancel.
 */
export const sendAppExitConfirmation = async (exitConfirmation: boolean) => {
  if (!isDesktop) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("exit_app_response", { confirmed: exitConfirmation });
  } catch (e) {
    console.error("Failed to send exit confirmation:", e);
  }
};

export const sendUpdateDownloadConfirmation = async (downloadConfirmation: boolean) => {
  if (!isDesktop) return;
  if (downloadConfirmation) {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      await invoke("start_update_download");
    } catch (e) {
      console.error("Update download failed:", e);
    }
  }
};

/**
 * Invoke the Tauri `check_for_update` command.
 *
 * Asks the Rust backend to query the update server. If a newer version is
 * found, Rust emits `confirm-download-update` which `registerIpcEvents`
 * already listens for — the AutoUpdateModal will open automatically.
 */
export const checkForDesktopUpdate = async () => {
  if (!isDesktop) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("check_for_update");
  } catch (e) {
    console.error("Update check failed:", e);
  }
};

// ── AmiBroker IPC functions ───────────────────────────────────────

export const notifyAmibrokerLogin = async (): Promise<any> => {
  if (!isDesktop) return { __invokeError: "Not running in desktop mode" };
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    return await invoke("user_login");
  } catch (e: any) {
    const errMsg = String(e);
    console.error("[AmiBroker] user_login failed:", errMsg);
    return { __invokeError: errMsg };
  }
};

export const notifyAmibrokerLogout = async (): Promise<void> => {
  if (!isDesktop) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("user_logout");
  } catch (e) {
    console.error("[AmiBroker] user_logout failed:", e);
  }
};

export const pushSymbolScripIdMap = async (map: Record<string, string>): Promise<void> => {
  if (!isDesktop) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("push_symbol_scrip_map", { symbolScripIdMap: map });
  } catch (e) {
    console.error("[AmiBroker] push_symbol_scrip_map failed:", e);
  }
};


export const pushTickDataToAmibroker = (data: any[]): void => {
  if (!isDesktop) return;
  invoke("push_ticker_data", { tickerData: data }).catch(() => {
    // Silent — high-frequency, must not spam the console
  });
};

export const pushBackfillDataToAmibroker = async (data: {
  symbol: string;
  candleInterval: string;
  payload: [string, number, number, number, number, number, number][];
}): Promise<void> => {
  if (!isDesktop) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("push_backfill_data", { backfillData: data });
  } catch (e) {
    console.error("[AmiBroker] push_backfill_data failed:", e);
  }
};

export const pushTodayFillDataToAmibroker = async (data: {
  symbol: string;
  candleInterval: string;
  payload: [string, number, number, number, number, number, number][];
}): Promise<void> => {
  if (!isDesktop) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("push_todayfill_data", { todayfillData: data });
  } catch (e) {
    console.error("[AmiBroker] push_todayfill_data failed:", e);
  }
};

/**
 * Push a watchlists object to AmiBroker.
 *
 * @param watchlists - Object whose keys are watchlist names and values are
 *   newline-delimited symbol strings, e.g. `{ "NSE_EQ": "RELIANCE\nINFY\n" }`.
 *   Matches the reference app's ipc-handler.ts signature exactly.
 */
export const createWatchlistInAmibroker = async (watchlists: Record<string, string>): Promise<void> => {
  if (!isDesktop) return;
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("create_watchlist", { watchlistData: watchlists });
};

export const deleteWatchlistInAmibroker = async (names: string[]): Promise<void> => {
  if (!isDesktop) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("delete_watchlist", { watchlistNames: names });
  } catch (e) {
    console.error("[AmiBroker] delete_watchlist failed:", e);
  }
};

export const checkAmibrokerHealth = async (): Promise<boolean> => {
  if (!isDesktop) return false;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    return await invoke<boolean>("check_amibroker_health");
  } catch {
    return false;
  }
};

export const getAmibrokerStatus = async (): Promise<any> => {
  if (!isDesktop) return null;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    return await invoke("get_amibroker_status");
  } catch (e) {
    console.error("[AmiBroker] get_amibroker_status failed:", e);
    return null;
  }
};

export const writeAmibrokerLog = async (
  level: string,
  module: string,
  method: string,
  message: string,
): Promise<void> => {
  if (!isDesktop) return;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("write_log", { level, module, method, message });
  } catch {
    // Silent
  }
};
